import React, {useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import EventsTableRow from '../eventTableRow/EventsTableRow';
import Loader from '../../../../../../component/loader/Loader';
import * as css from './EventsTable.css';
import CustomScrollbar from '../../../../../../component/customScrollbar/CustomScrollbar';
import {EventsTableProps, EventData} from '../../utils/types';
import {transformEventToTableData, createSelectedEventIdsSet, createSelectedEventsMap} from '../../utils/utils';
import useMobile from '../../../../../../hooks/useMobile';
import EventDetailsPop from '../eventDetailsPop/EventDetailsPop';
import useShow from '../../../../../../hooks/useShow';

function EventsTable({events, selectedEvents, onEventSelect, onEventKindChange, loading = null, onScrollEnd, showEventKind = false}: EventsTableProps) {
    const {t} = useTranslation('subscriptions');
    const tableBodyRef = useRef(null);
    const isMobile = useMobile();
    const [selectedEventForPopup, setSelectedEventForPopup] = useState<EventData | null>(null);
    const detailsPopup = useShow();

    // Prepare data for TableRow component using utility function with Set for better performance
    const selectedEventIds = createSelectedEventIdsSet(selectedEvents);
    const selectedEventsMap = createSelectedEventsMap(selectedEvents);
    
    const tableData: EventData[] = events.map((event): EventData => 
        transformEventToTableData(event, selectedEventIds, selectedEventsMap)
    );

    const handleEvent = (type: string, data: any) => {
        if(type === 'change' && data.name === 'selected') {
            onEventSelect(data.id, data.value);
        } else if(type === 'change' && data.name === 'eventKind') {
            onEventKindChange(data.id, data.value === 'mandatory');
        }
    };

    const handleOpenDetailsPopup = (event: EventData) => {
        setSelectedEventForPopup(event);
        detailsPopup.open();
    };

    const handleCloseDetailsPopup = () => {
        detailsPopup.close();
        setSelectedEventForPopup(null);
    };

    return (
        <>
            <css.table>
                {!isMobile && <css.tableHeader>
                    <css.headerCell>{t('selection')}</css.headerCell>
                    <css.headerCell>{t('eventName')}</css.headerCell>
                    <css.headerCell>{t('date')}</css.headerCell>
                    <css.headerCell>{t('time')}</css.headerCell>
                    <css.headerCell>{t('category')}</css.headerCell>
                    <css.headerCell>{t('location')}</css.headerCell>
                    <css.headerCell>{t('status')}</css.headerCell>
                    {showEventKind && <css.headerCell>{t('mandatoryEvents')}</css.headerCell>}
                </css.tableHeader>}
                <CustomScrollbar contentRef={tableBodyRef} cssStyle={'inset-inline-end: 4px;'} maxBottom={0}/>
                <css.tableBody > 
                    <Loader containerRef={tableBodyRef} height="fit-content" XHR={loading} onScrollEnd={onScrollEnd}>
                        {tableData.length > 0 && tableData.map(event => (
                            <EventsTableRow 
                                key={event.id}
                                event={event}
                                handleEvent={handleEvent}
                                showEventKind={showEventKind}
                                onOpenDetailsPopup={isMobile ? handleOpenDetailsPopup : undefined}
                            />
                        ))}
                    </Loader>
                </css.tableBody>
            </css.table>
            {isMobile && selectedEventForPopup && detailsPopup.isOpen && (
                <EventDetailsPop
                    event={selectedEventForPopup}
                    show={detailsPopup}
                    onClose={handleCloseDetailsPopup}
                    handleEvent={handleEvent}
                    showEventKind={showEventKind}
                />
            )}
        </>
    );
}

export default EventsTable;
